import { useState, useRef, useEffect, useCallback } from 'react';
import { useImmer } from 'use-immer'
import { Sidebar, SidebarButton, Header, CircularMenuButton, Scrollable, Pagination, useIsFormSubmitting, useHandleSubmitError, SidebarSpacing, SidebarHeaderProvider } from '@/components/DashboardComponents'
import { useNavigate, useLoaderData, Form, useSubmit, useRouteLoaderData, useParams } from 'react-router'
import { Button, Textbox, Spacing, RadioGroup, RadioOption, Checkbox, TextboxList, HalfSpacing } from '@/form/FormComponents'
import { Icon } from '@/components/Icon'
import { FormProvider, FormObjectGroup, FormArrayGroup, FormDisabled, FormDataProvider, FormSubmitProvider } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useModal } from '@/components/Modal';
import { RubricEditor } from '@/pages/RubricLayout';
import { Rubrics } from '@/pages/NewScenarioLayout';
import { compareRubricArrays } from '@/shared/utils'
import { useContinueUnlessBlocked } from '@/systems/Blocker';
import { publicAction } from '@/data/loaders';

export function NewAssessmentLayout({ is_new }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("basic_details");
  const [in_rubric_editor, setInRubricEditor] = useState(false);
  const loader_data = useLoaderData().data.value;

  const scenario_data = is_new ? loader_data.data : loader_data.scenario_data;
  const scenario_id = is_new ? loader_data._id : loader_data.scenario_id;
  const scenario_in_sync = is_new ? true : loader_data._in_sync;
  const scenario_edited_locally = is_new ? false : loader_data.scenario_version_id == null;
  const scenario_has_permission_to_edit = is_new ? true : loader_data._has_permission_to_edit_scenario;

  const [initial_rubrics, updateInitialRubrics] = useImmer((loader_data.rubrics || []).map(r => (is_new ? {...r, from_scenario: true} : r)));
  const [rubrics, updateRubrics] = useImmer(initial_rubrics);

  const course = useRouteLoaderData("course").data.value;
  const submit = useSubmit();
  const form = useRef();
  const submitting = useIsFormSubmitting();
  useHandleSubmitError();

  const { assessment_id } = useParams();

  const initial_data = is_new ? null : (() => {
    const { due_date, ...rest } = loader_data.data;
    const date = new Date(due_date);
    return {
      ...rest,
      ...(due_date ? {
        add_due_date: true,
        due_date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
        due_time: `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`,
      } : {
        add_due_date: false,
        due_date: '',
        due_time: '23:59',
      }),
    };
  })();

  function submitForm({ formatted_data }) {

    const { add_due_date, due_date, due_time, ...rest } = formatted_data;
    const new_formatted_data = {
      ...rest,
      ...(add_due_date ? {
        due_date: Date.parse(`${due_date}T${due_time}`)
      } : {
        due_date: null,
      }),
    };

    const formatted_rubrics = rubrics.map(rubric => {
      return {
        title: rubric.title,
        scoring: rubric.scoring,
        levels: rubric.levels,
        sections: rubric.sections,
        grading_instructions: rubric.grading_instructions,
        _id: rubric._id,
        version_id: rubric.version_id,
        local_id: rubric.local_id,
        from_scenario: rubric.from_scenario,
      }
    })

    submit({ data: new_formatted_data, rubrics: formatted_rubrics }, { method: "post", encType: "application/json" });
  }

  function handleValidateFailed({ element }) {
    setTab(element.closest("[tabname]").getAttribute("tabname"));
  }

  const goBackCallback = useCallback(() => {
    setInRubricEditor(false);
  }, [setInRubricEditor]);

  const submitCallback = useCallback(({ rubric }) => {
    if (in_rubric_editor.index != null) {
      updateRubrics(draft => {
        draft[in_rubric_editor.index] = rubric;
      });
      if (rubric._id) {
        updateInitialRubrics(draft => {
          draft[draft.findIndex(r => r._id == rubric._id)] = rubric;
        });
      }
    } else {
      updateRubrics(draft => {
        draft.push(rubric);
      });
    }
    setInRubricEditor(false);
  }, [in_rubric_editor, updateRubrics, setInRubricEditor, updateInitialRubrics]);

  return (
    <>
      <FormProvider onSubmit={submitForm} onValidateFailed={handleValidateFailed} initial_data={initial_data} has_changed={!compareRubricArrays(initial_rubrics, rubrics)} unsaved_modal="new_assessment">
        <Form id="create_assessment" method="post" ref={form} onSubmit={(event) => event.preventDefault()}>
          <div className="dashboard_container">
            {in_rubric_editor ?
              <>
                <RubricEditor goBackCallback={goBackCallback} from_scenario_editor={true} from_assessment={true} rubric={in_rubric_editor.rubric} submitCallback={submitCallback}/>
              </> :
              <>
                <header className="dashboard_topbar">
                  <div className="topbar_left">
                    <CircularMenuButton onClick={() => navigate(-1)} icon="arrow-left"/>
                    <div className="topbar_title">{initial_data ? "Edit" : "New"} assessment</div>
                  </div>
                </header>
                <div className="dashboard_main">
                  <SidebarHeaderProvider>
                    <Sidebar>
                      <SidebarButton onClick={() => {setTab("basic_details")}} name="Assessment details" selected={tab == "basic_details"}/>
                      {/* <SidebarSpacing/> */}
                      <SidebarButton onClick={() => {setTab("rubrics")}} name="Rubrics" selected={tab == "rubrics"}/>
                    </Sidebar>
                    <Scrollable className="dashboard_content" depencencies={[tab, in_rubric_editor]}>
                      <BasicDetails visible={tab == "basic_details"} editing_scenario={initial_data ? true : false} course_name={course.name} course_id={course._id} scenario_data={scenario_data} scenario_id={scenario_id} scenario_in_sync={scenario_in_sync} scenario_has_permission_to_edit={scenario_has_permission_to_edit} assessment_id={assessment_id} scenario_edited_locally={scenario_edited_locally}/>
                      <Rubrics visible={tab == "rubrics"} editing_scenario={initial_data ? true : false} is_assessment={true} setInRubricEditor={setInRubricEditor} rubrics={rubrics} updateRubrics={updateRubrics}/>
                      <Pagination show_continue={!initial_data && tab != "rubrics"} getContainerElement={() => document.querySelector("#create_assessment")}>
                        <FormSubmitProvider>
                          {({handleSubmit, has_changed}) => {
                            if (tab == "rubrics" || initial_data) {
                              return <Button className="primary" text={initial_data ? "Save changes" : "Create assessment"} onClick={handleSubmit} disabled={submitting || (initial_data && !has_changed)}/>
                            } else {
                              return <Button text="Continue" className="primary" icon="chevron-right" icon_right={true} onClick={handleSubmit}/>
                            }
                          }}
                        </FormSubmitProvider>
                      </Pagination>
                    </Scrollable>
                  </SidebarHeaderProvider>
                </div>
              </>
            }
          </div>
        </Form>
      </FormProvider>
    </>
  )
}

function BasicDetails({visible, editing_scenario, course_name, course_id, scenario_data, scenario_id, scenario_in_sync, scenario_has_permission_to_edit, assessment_id, scenario_edited_locally}) {
  const navigate = useNavigate();
  const continueUnlessBlocked = useContinueUnlessBlocked();

  const [openNoPermissionModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Edits won't be saved back to the original scenario">
        <ModalContent>
          {`You don't have access to edit the original scenario, so any changes you make will only be saved for this assessment.`}
          <br/>
          <br/>
          {`To avoid this, you could ask the owner of the scenario to make the changes instead.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Edit just for this assessment" onClick={() => {
            closeModal();
            continueUnlessBlocked(() => {
              navigate(`/courses/${course_id}/assessments/${assessment_id}/edit_scenario`, { replace: true });
            });
          }}/>
        </ModalButtons>
      </Modal>
    )
  });

  const [openEditWarningModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Edits won't be saved back to the original scenario">
        <ModalContent>
          {`You are about to edit a copy of the original scenario. Any changes you make will only be saved for this assessment.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Edit just for this assessment" onClick={() => {
            closeModal();
            continueUnlessBlocked(() => {
              navigate(`/courses/${course_id}/assessments/${assessment_id}/edit_scenario`, { replace: true });
            });
          }}/>
        </ModalButtons>
      </Modal>
    )
  });

  // const [openNoPermissionModal] = useModal(({ closeModal }) => {
  //   return (
  //     <Modal title="You need access">
  //       <ModalContent>
  //         {`You don't have access to edit this scenario. Ask the owner of the scenario to add you as a collaborator (coming soon).`}
  //         <br/>
  //         <br/>
  //         {`Alternatively, you can make edits just for this assessment, but any changes you make won't be saved back to the original scenario.`}
  //       </ModalContent>
  //       <ModalButtons>
  //         <Button text="Cancel" className="hollow" onClick={closeModal}/>
  //         <Button text="Edit just for this assessment" onClick={() => {
  //           closeModal();
  //           navigate(`/scenarios/a-${assessment_id}`);
  //         }}/>
  //       </ModalButtons>
  //     </Modal>
  //   )
  // });

  const handleClickEditScenario = () => {
    if (scenario_in_sync) {
      if (scenario_has_permission_to_edit) {
        continueUnlessBlocked(() => {
          navigate(`/scenarios/${scenario_id}?assessment_id=${assessment_id || "new"}&course_id=${course_id}`, { replace: true });
        });
      } else {
        openNoPermissionModal();
      }
    } else {
      openEditWarningModal();
    }
  }

  const [openSaveToMyScenariosModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      setLoading(true);

      publicAction("saveAssessmentScenario", {
        title: formatted_data.title,
        assessment_id: assessment_id,
        course_id: course_id,
      }).then(({ data }) => {

        closeModal();
        navigate(".", { replace: true });

      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong. Please try again."})
      })

    }

    return (
      <Modal title="Save to my scenarios">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" title="Scenario title" onEnter={handleSubmit} default_value={scenario_data.title || ""} autofocus={true} required={"Please enter a title for this scenario"}/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Save scenario" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const handleClickSaveToMyScenarios = () => {
    openSaveToMyScenariosModal();
  }

  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="basic_details">
      <Header title="Assessment details"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          {editing_scenario ? null : <>
            <label>Course</label>
            <div className="selected_scenario_box">
              <div>
                <div>
                  <Icon name="book-open-variant-outline"/>
                </div>
                <div>{course_name}</div>
              </div>
            </div>
            <Spacing/>
          </>}
          <label>Scenario</label>
          <div className="selected_scenario_box">
            <div>
              <div>
                <Icon name="forum-outline"/>
              </div>
              <div>{!scenario_in_sync ? <><i>{scenario_edited_locally ? `Edited: ` : `Old copy: `}</i>{scenario_data.title}</> : scenario_data.title}</div>
            </div>
            <div className="buttons small right">
              <Button text={scenario_edited_locally ? "Edit" : "Edit scenario"} className="small hollow" icon="pencil-outline" onClick={handleClickEditScenario}/>
              {scenario_edited_locally && <Button text="Save to my scenarios" className="small hollow" icon="forum-plus-outline" onClick={handleClickSaveToMyScenarios}/>}
            </div>
          </div>
          <Spacing/>
          <Textbox name="title" title="Assessment title" autofocus={visible} required="Please enter an assessment title" hint={"Learners will see this instead of the scenario title."}/>
          <Spacing/>
          <Textbox name="description" title="Instructions (Optional)" multiline={true} markdown={true} height="long"/>
          <Spacing/>
          <Checkbox text="Due date:" name="add_due_date" default_value={false}/>
          <HalfSpacing/>
          <FormDataProvider fields={["add_due_date"]}>{({data}) => (
            <FormDisabled obviated={!data.add_due_date} indented={true}>
              <div className="due_date_time_section">
                <Textbox name="due_date" title="Date" type="date" required="Please choose date"/>
                <Textbox name="due_time" title="Time" type="time" default_value="23:59" required="Please choose time"/>
              </div>
              <HalfSpacing/>
              <div style={{display: "none"}}>
                <Checkbox name="allow_late_submissions" text="Allow late submissions" default_value={true}/>
              </div>
            </FormDisabled>
          )}</FormDataProvider>
        </div>
      </div>
    </div>
  )
}

